/**
 * SessionList Component
 *
 * Display list of ACF sessions with actions
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Bot,
  Plus,
  MoreVertical,
  MessageSquare,
  Trash2,
  Archive,
  Play,
  Pause,
  Clock,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  useACFSessions,
  useDeleteACFSession,
  useUpdateACFSession,
  type ACFSession,
  type ACFSessionStatus,
} from '@/hooks/useACF';
import { useToast } from '@/shared/hooks/useToast';

interface SessionListProps {
  workspaceId: string;
  onSelectSession?: (sessionId: string) => void;
  onCreateNew?: () => void;
  selectedSessionId?: string;
  className?: string;
}

/**
 * Status configuration
 */
const statusConfig: Record<
  ACFSessionStatus,
  {
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    icon: React.ComponentType<{ className?: string }>;
    label: string;
  }
> = {
  active: { variant: 'default', icon: Play, label: 'Active' },
  paused: { variant: 'secondary', icon: Pause, label: 'Paused' },
  completed: { variant: 'outline', icon: Clock, label: 'Completed' },
  archived: { variant: 'outline', icon: Archive, label: 'Archived' },
};

/**
 * Format relative time
 */
function formatRelativeTime(date: string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * SessionList - Display and manage ACF sessions
 */
export function SessionList({
  workspaceId,
  onSelectSession,
  onCreateNew,
  selectedSessionId,
  className,
}: SessionListProps): React.JSX.Element {
  const { toast } = useToast();
  const { data, isLoading, error, refetch } = useACFSessions(workspaceId);
  const deleteSession = useDeleteACFSession();
  const updateSession = useUpdateACFSession();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [sessionToDelete, setSessionToDelete] = React.useState<ACFSession | null>(null);

  const handleDeleteClick = (session: ACFSession): void => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!sessionToDelete) return;

    try {
      await deleteSession.mutateAsync({
        sessionId: sessionToDelete.id,
        workspaceId,
      });
      toast({
        title: 'Session Deleted',
        description: 'The session has been deleted.',
      });
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    } catch (err) {
      toast({
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'Failed to delete session',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (
    session: ACFSession,
    status: ACFSessionStatus
  ): Promise<void> => {
    try {
      await updateSession.mutateAsync({
        sessionId: session.id,
        updates: { status },
      });
      toast({
        title: 'Status Updated',
        description: `Session marked as ${status}`,
      });
    } catch (err) {
      toast({
        title: 'Update Failed',
        description: err instanceof Error ? err.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn('border-destructive', className)}>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Failed to load sessions'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sessions = data?.sessions ?? [];

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>AI Coding Sessions</CardTitle>
              <CardDescription>
                {sessions.length === 0
                  ? 'No sessions yet'
                  : `${sessions.length} session${sessions.length !== 1 ? 's' : ''}`}
              </CardDescription>
            </div>
            {onCreateNew && (
              <Button onClick={onCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                New Session
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bot className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No sessions yet</h3>
              <p className="text-muted-foreground mb-4 max-w-sm">
                Start an AI coding session to get help with your code, ask questions,
                or brainstorm ideas.
              </p>
              {onCreateNew && (
                <Button onClick={onCreateNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Start Session
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => {
                const statusInfo = statusConfig[session.status];
                const StatusIcon = statusInfo.icon;
                const isSelected = session.id === selectedSessionId;

                return (
                  <div
                    key={session.id}
                    className={cn(
                      'flex items-center justify-between p-3 border rounded-lg transition-colors cursor-pointer',
                      isSelected
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-accent/50'
                    )}
                    onClick={() => onSelectSession?.(session.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={cn(
                          'p-2 rounded-lg',
                          isSelected ? 'bg-primary/20' : 'bg-primary/10'
                        )}
                      >
                        <MessageSquare className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate text-sm">
                          {session.title || 'Untitled Session'}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={statusInfo.variant} className="text-xs gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.label}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(session.updatedAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectSession?.(session.id);
                          }}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {session.status === 'active' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(session, 'paused');
                            }}
                          >
                            <Pause className="h-4 w-4 mr-2" />
                            Pause
                          </DropdownMenuItem>
                        )}
                        {session.status === 'paused' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(session, 'active');
                            }}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Resume
                          </DropdownMenuItem>
                        )}
                        {session.status !== 'archived' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(session, 'archived');
                            }}
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(session);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this session? This will permanently remove
              the conversation history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSession.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
